import { useId } from 'react';
import { Link, useRouteContext } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { diamondGlows, DiamondGrid } from './primitives';
import { PartnerLogo } from './partner-logo';
import { Belt, Wheel } from './belt';
import { buildPortalHref } from './portal-href';

/**
 * "Empresas que ya generan impacto con Worldcoo" — the partner-merchant logo wheel.
 * Logos are REAL merchants from `getWorldcooGlobalStats` → `partnerMerchants` (`{ id, name,
 * portalId, logo }`), already filtered upstream to merchants running a campaign *right now* and
 * ordered by amount raised (biggest first). Only merchants with a logo are rendered; those with a
 * `portalId` link to their public donor portal (`/{portalId}`) in a new tab, the rest are shown but
 * not clickable.
 *
 * Desktop is a single "ferris wheel": one shared belt scrolls down the right side and up the left (a
 * single loop, not two independent columns). Mobile keeps two horizontal marquee rows. Both are the
 * CSS-only belts from `./belt` — see that file for why they are not `motion/react` any more.
 */
export type PartnerMerchant = { id: string; name?: string; portalId?: string; logo?: { location: string } };

function LogoCard({ merchant }: { merchant: PartnerMerchant }) {
	// `pointer-events-none` so a press on the image doesn't start a native drag; the hit falls through
	// to the wrapping <a>. `xl` is the biggest box that fits the card — keep in sync with `./belt`.
	return (
		<div className="pointer-events-none flex h-24 w-56 items-center justify-center rounded-2xl bg-white px-5 shadow-[0_8px_24px_0_rgba(77,33,0,0.06)] select-none">
			{merchant.logo ? (
				// `eager`: every logo on the belt is fetched with the page, not when it first comes into
				// sight. A belt brings its own cards into view, so a lazy logo starts loading only once it
				// is already on screen — the card slides in blank and the mark appears a moment later,
				// which is the flicker along both edges of the row. See `Image`'s `eager`.
				<PartnerLogo src={merchant.logo.location} alt={merchant.name ?? merchant.id} size="xl" eager />
			) : (
				<span className="text-ink truncate text-sm font-semibold">{merchant.name}</span>
			)}
		</div>
	);
}

/**
 * Wraps a logo in a link to the merchant's public portal when it has one; otherwise a plain,
 * non-interactive box.
 */
function LogoLink({
	merchant,
	baseUrl,
	className,
}: {
	merchant: PartnerMerchant;
	baseUrl: string;
	className?: string;
}) {
	if (!merchant.portalId) {
		return (
			<div className={className}>
				<LogoCard merchant={merchant} />
			</div>
		);
	}
	return (
		<a
			href={buildPortalHref(baseUrl, merchant.portalId)}
			// A merchant's portal is a different site on a different subdomain, so it opens in its own tab
			// rather than replacing the page the reader was on — the logo wheel is something you browse,
			// and following one logo should not cost you the section.
			target="_blank"
			rel="noopener noreferrer"
			aria-label={merchant.name ?? merchant.id}
			title={merchant.name}
			className={cn('block', className)}
			draggable={false}
		>
			<LogoCard merchant={merchant} />
		</a>
	);
}

/** px per second, matching the desktop wheel so both read as one motion. */
const MOBILE_SPEED = 32;

/** Mobile: a horizontal row of logos. The per-item right margin is what the loop needs — see `Belt`. */
function LogoRowMobile({
	merchants,
	baseUrl,
	reverse,
}: {
	merchants: PartnerMerchant[];
	baseUrl: string;
	reverse?: boolean;
}) {
	// An empty row still takes its place. This is what the section looks like while its merchants are
	// still streaming in, and a row that collapses to nothing and then springs to 120px is the whole
	// section jumping under the reader — see the note on `Partners`. `h-24` is `LogoCard`'s own height,
	// and the padding is the row's, so the empty row measures exactly what the full one will.
	if (merchants.length === 0) {
		return (
			<div aria-hidden className="pt-1 pb-5">
				<div className="h-24" />
			</div>
		);
	}
	return (
		// Two separate things used to slice the cards' shadow off top and bottom, and BOTH have to go.
		// `overflow-hidden` was one: `hidden` on a single axis makes the other a clipping context too,
		// so it becomes `overflow-x-clip` — `clip` is the one overflow value allowed to pair with a
		// visible cross axis. The mask was the other, and the less obvious one: `mask-image` paints
		// only inside the border box, so it clipped the shadow even once the overflow was fixed. Hence
		// the vertical padding — it is exactly the shadow's reach (a 24px blur extends 12px, offset 8px
		// down, so 4px above and 20px below), sized to grow the border box rather than to space the row.
		<div className="overflow-x-clip [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)] pt-1 pb-5">
			<Belt speed={MOBILE_SPEED} reverse={reverse}>
				{merchants.map((merchant, i) => (
					// oxlint-disable-next-line react/no-array-index-key
					<LogoLink key={i} merchant={merchant} baseUrl={baseUrl} className="mr-3 shrink-0" />
				))}
			</Belt>
		</div>
	);
}

/** Centered title + CTA, shared by the desktop overlay and the mobile layout. */
function Center() {
	const { t } = useLingui();
	return (
		<div className="flex flex-col items-center gap-6 text-center">
			<h2 className="max-w-md text-3xl font-medium tracking-tight md:text-4xl">
				{t`Companies already making an impact with Worldcoo`}
			</h2>
			{/* The heading above says "companies", so the CTA under it goes to that questionnaire. */}
			<Link
				to="/www/contact/$audience"
				params={{ audience: 'company' }}
				className="bg-brand hover:bg-brand-strong pointer-events-auto flex items-center gap-2 rounded-full py-2.5 pr-2.5 pl-5 font-medium text-black transition-colors"
			>
				{t`Contact us`}
				<span className="bg-ink grid size-6 place-items-center rounded-full text-white">
					<ArrowUpRight className="size-4" />
				</span>
			</Link>
		</div>
	);
}

/**
 * The dangling line-and-dot below the section.
 *
 * Drawn in SVG rather than as three centred `<div>`s, which is what it was, because CSS SNAPS a
 * painted box to whole device pixels while SVG shapes are ANTIALIASED. The ring is 12px, an even
 * width, so its ink falls symmetrically either side of the centre — but a `w-px` line cannot do that.
 * It has to occupy one whole column, and it took the one on the left. Measured on the page at a 1.25
 * device-pixel ratio: the line's ink centroid sat exactly 0.5px left of the ring's, which is the
 * "not quite centred" a review caught. The centres agree in CSS to three decimals, so nothing in the
 * layout was ever wrong — only the rasterisation. Inside an SVG both are antialiased about the same
 * axis and cannot disagree, at any ratio or any scroll position.
 *
 * Two elements rather than one: the line stretches with the breakpoint (`h-28` → `md:h-36`) and the
 * ring must not, so the line takes `preserveAspectRatio="none"` — safe for a shape with no aspect to
 * lose — while the ring keeps its intrinsic box. Their boxes are centred by the same `items-center`
 * as before, and each shape is centred inside its own box, so the two centres coincide by
 * construction rather than by arithmetic.
 *
 * `useId` for the gradient: a literal id would collide with itself while this section and its own
 * Suspense fallback are both mounted, and the browser keeps only the first.
 */
function Connector() {
	const fade = useId();
	return (
		<div className="text-brand relative mt-4 flex flex-col items-center xl:-mt-24" aria-hidden>
			<svg className="h-28 w-3 md:h-36" viewBox="0 0 12 144" preserveAspectRatio="none">
				<defs>
					<linearGradient id={fade} x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor="currentColor" stopOpacity="0" />
						<stop offset="100%" stopColor="currentColor" stopOpacity="1" />
					</linearGradient>
				</defs>
				<rect x="5.5" y="0" width="1" height="144" fill={`url(#${fade})`} />
			</svg>
			{/* Ring and tail in one drawing: 12px ring (2px stroke, white fill) over a 10px tail. */}
			<svg width="12" height="22" viewBox="0 0 12 22">
				<circle cx="6" cy="6" r="5" fill="white" stroke="currentColor" strokeWidth="2" />
				<rect x="5.5" y="12" width="1" height="10" fill="currentColor" />
			</svg>
		</div>
	);
}

export function Partners({
	connector = true,
	...props
}: {
	/**
	 * Omitted while the merchants are still on their way: the section then renders its own shell —
	 * heading, CTA, an empty wheel, two empty rows — at exactly the height it will have once they
	 * land. That is what makes `<Partners />` usable as this section's `Suspense` fallback, and it is
	 * the only arrangement that cannot drift: a hand-written placeholder is a second copy of this
	 * section's geometry, and the first time the wheel or the paddings change it becomes a lie.
	 */
	merchants?: PartnerMerchant[];
	/**
	 * The dangling line-and-dot below the section, which ties it to whatever comes next. It is part of
	 * the homepage composition, so pages that place this section elsewhere turn it off rather than
	 * leaving a thread pointing at a section it was never drawn for.
	 */
	connector?: boolean;
}) {
	const merchants = props.merchants ?? [];
	// Current origin (scheme + host), used to build each merchant's portal subdomain URL.
	const { baseUrl } = useRouteContext({ from: '__root__' });
	// Only merchants with a real logo make it into the wheel.
	const logos = merchants.filter((m) => m.logo);

	return (
		<section className="relative overflow-hidden bg-white">
			{/* Same glow shape as the hero, in the single position Figma gives this section: centred, 36px
			    above the middle. `opacity` 1 because the glow's own 0.4 alpha is the intensity. */}
			<DiamondGrid opacity={1} masks={[diamondGlows({ x: '50%', y: 'calc(50% - 36px)' })]} />

			<div className="relative mx-auto max-w-[1440px] px-6 py-20 md:py-28 xl:px-10">
				<div className="relative hidden xl:block">
					<Wheel items={logos} renderItem={(merchant) => <LogoLink merchant={merchant} baseUrl={baseUrl} />} />
					<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
						<Center />
					</div>
				</div>

				<div className="xl:hidden">
					<Center />
					<div className="relative mt-8 flex flex-col gap-3">
						<LogoRowMobile merchants={logos.filter((_, i) => i % 2 === 0)} baseUrl={baseUrl} />
						<LogoRowMobile merchants={logos.filter((_, i) => i % 2 === 1)} baseUrl={baseUrl} reverse />
					</div>
				</div>
			</div>

			{connector && <Connector />}
		</section>
	);
}
